import type { ElementRef, OnDestroy } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, viewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { GAMES_DETAILS, GameTypes, TransactionWay } from '@dev-fast/types';
import { TranslatePipe } from '@ngx-translate/core';
import Swiper from 'swiper';
import { Autoplay, FreeMode, Navigation } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types';

import { AuthService } from '@app/auth';
import { LanguageService } from '@app/core/language-service';
import type { IQuest } from '@app/core/quest';
import { EQuestStatus } from '@app/core/quest';
import { StaticUrlPipe } from '@app/shared/pipes/static-url.pipe';
import { MatIcon } from '@app/ui/components/icon/icon.component';

import { NorthPoleProgressBarComponent } from '../../../../north-pole/components/north-pole-progress-bar/north-pole-progress-bar.component';

@Component({
  selector: 'app-april-madness-quest-modal',
  imports: [TranslatePipe, MatIcon, NorthPoleProgressBarComponent, StaticUrlPipe],
  templateUrl: './april-madness-quest-modal.component.html',
  styleUrl: './april-madness-quest-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AprilMadnessQuestModalComponent implements OnDestroy {
  readonly #dialogRef = inject(MatDialogRef<AprilMadnessQuestModalComponent>);
  readonly quest = inject<IQuest>(MAT_DIALOG_DATA);
  readonly #router = inject(Router);
  readonly #authService = inject(AuthService);
  readonly #languageService = inject(LanguageService);

  swiper: Swiper | undefined;

  readonly swiperParams: SwiperOptions = {
    modules: [Navigation, FreeMode, Autoplay],
    centeredSlides: true,
    observeParents: true,
    observer: true,
    init: false,
    slidesPerView: 'auto',
    speed: 500,
    freeMode: {
      enabled: true,
      sticky: true,
      momentum: false,
      momentumBounce: false,
    },
    autoplay: {
      delay: 10_000,
      disableOnInteraction: false,
      pauseOnMouseEnter: true,
    },
  };

  readonly activeSlide = signal(0);

  readonly gameIcon = computed(() => {
    const gameTypeId = this.quest.task.gameTypeId || 0;
    const gameDetails = Object.values(GAMES_DETAILS).find((game) => game.gameType === gameTypeId);

    return gameDetails?.icon || '';
  });

  readonly swiperElement = viewChild<ElementRef>('swiper');
  readonly questStatus = EQuestStatus;

  constructor() {
    effect(() => {
      if (this.swiperElement()) {
        this.#initializeSwiper();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.swiper) {
      this.swiper.destroy();
      this.swiper = undefined;
    }
  }

  close(): void {
    this.#dialogRef.close();
  }

  toPrevSlide(): void {
    this.swiper?.slidePrev();
  }

  toNextSlide(): void {
    this.swiper?.slideNext();
  }

  toSlide(step: number): void {
    this.swiper?.slideTo(step);
  }

  onLogin(): void {
    this.close();
    this.#authService.openLoginProvidersModal();
  }

  redirectToGame = (gameTypeId?: GameTypes): void => {
    if (!gameTypeId) {
      return;
    }
    this.close();

    if (gameTypeId > 0) {
      const dictionary: Record<string, string> = {
        wheel: 'fortune-wheel',
      };
      const gameUrl = GameTypes[gameTypeId].toLowerCase().replace(/_/g, '-');
      const mappedGameUrl = dictionary[gameUrl] || gameUrl;
      this.#router.navigate([this.#languageService.getCurrentLangUrl(`/game/${mappedGameUrl}`)]);
    } else {
      this.#router.navigate([{ outlets: { modal: ['transaction', TransactionWay.REFILL] } }], { queryParamsHandling: 'preserve' });
    }
  };

  #initializeSwiper(): void {
    this.swiper = new Swiper(this.swiperElement()?.nativeElement, {
      ...this.swiperParams,
      on: {
        activeIndexChange: (swiper) => {
          this.activeSlide.set(swiper.activeIndex);
        },
      },
    });

    if (this.swiper) {
      this.swiper.init();
    }
  }
}
